import { labDayOf, labDayStart, labDaysBefore, labMonthStart, labWeekStart } from '../time/labDay';

/**
 * The counts above the Doctors and the Patients lists, and the list narrowed
 * to one of them. The request of 6 October 2026 (fifth), section 1.
 *
 * Every count is the server's, on the lab's calendar: the week begins on
 * Monday at midnight in Dubai and the month on its first day. Pressing a count
 * narrows the list below it to exactly the records that count counts, which is
 * the `show` of `GET /doctors` and `GET /patients`: one word for the count and
 * for the filter, so the number on the card is the number of rows under it.
 */

/** `GET /doctors/summary`. */
export interface DoctorSummary {
  total: number;
  /** Records made on or after Monday of this week. */
  newThisWeek: number;
  /** Records made on or after the first of this month. */
  newThisMonth: number;
  /** Doctors with a case, the deleted left out, booked in the last thirty lab days, today among them. */
  returning: number;
  /** Doctors with at least one case that is not dispatched and not archived. */
  withActiveCases: number;
  /** Doctors whose number or address the conversion left for a person to put right. */
  needsReview: number;
  /** Doctors with a number or an address still waiting to be converted. Not a `show`. */
  oldFormat: number;
}

/** `GET /patients/summary`. */
export interface PatientSummary {
  total: number;
  newThisWeek: number;
  newThisMonth: number;
  withActiveCases: number;
}

/** What the list of doctors may be narrowed to: the `show` of `GET /doctors`. */
export const DOCTOR_SHOWS = [
  'newThisWeek',
  'newThisMonth',
  'returning',
  'withActiveCases',
  'needsReview',
] as const;
export type DoctorShow = (typeof DOCTOR_SHOWS)[number];

/** The same for patients: the `show` of `GET /patients`. */
export const PATIENT_SHOWS = ['newThisWeek', 'newThisMonth', 'withActiveCases'] as const;
export type PatientShow = (typeof PATIENT_SHOWS)[number];

/** A `show` read from an address or a request: the word when it is one of the list's own, else null. */
export function doctorShowOf(text: string | null | undefined): DoctorShow | null {
  return DOCTOR_SHOWS.find((show) => show === text) ?? null;
}

export function patientShowOf(text: string | null | undefined): PatientShow | null {
  return PATIENT_SHOWS.find((show) => show === text) ?? null;
}

/** How far back "returning" reaches, in lab days, today among them. */
export const RETURNING_DAYS = 30;

/**
 * The three instants the counts are measured from, for a given now: the start
 * of this week and of this month on the lab's calendar, and the start of the
 * lab day twenty nine days before today, from which a doctor with a new case
 * is returning.
 */
export function summaryStarts(now: string | number | Date): {
  week: Date;
  month: Date;
  returning: Date;
} {
  const today = labDayOf(now);
  return {
    week: labDayStart(labWeekStart(today)),
    month: labDayStart(labMonthStart(today)),
    returning: labDayStart(labDaysBefore(today, RETURNING_DAYS - 1)),
  };
}
